import { FormatDataType, parseFormatString } from './formatParser';
import { Formatters } from '../../gen-src/Localize';
import { Messages } from '../localization';
import { MetricPillValue, MetricValueRole } from '../models';

const DEFAULT_PILL_CHARACTERS = 10;

enum IncludeSign {
  NEVER,
  NEGATIVE,
  ALWAYS,
}

export function formatValueAsMetricNumber(value?: number, formatString?: string, maxCharacters?: number): string {
  return formatMetricValueForDisplay(value, formatString || '', IncludeSign.NEGATIVE, maxCharacters);
}

export function formatValueAsRelativeNumber(value?: number, formatString?: string, maxCharacters?: number): string {
  return formatMetricValueForDisplay(value, formatString || '', IncludeSign.ALWAYS, maxCharacters);
}

// Note: we don't want to use a format string for e.g. currency symbol, since this is always a percentage.
export function formatValueAsRelativePercentage(value?: number, maxCharacters?: number): string {
  return formatMetricValueForDisplay(value, 'p', IncludeSign.ALWAYS, maxCharacters);
}

// Note: we don't want to use a format string for e.g. currency symbol, since this is always a percentage.
export function formatValueAsUnsignedPercentage(value?: number, maxCharacters?: number): string {
  return formatMetricValueForDisplay(value, 'p', IncludeSign.NEVER, maxCharacters);
}

export function formatAbsoluteValue(value?: number, formatString?: string, maxCharacters?: number): string {
  return formatMetricValueForDisplay(value, formatString || '', IncludeSign.NEVER, maxCharacters);
}

export const formatPillValue = (v: MetricPillValue, formatString?: string, maxCharacters?: number): string => {
  maxCharacters = maxCharacters || DEFAULT_PILL_CHARACTERS;

  // tslint:disable-next-line: switch-default
  switch (v.role) {
    case MetricValueRole.CURRENT:
      return formatValueAsMetricNumber(v.value, formatString, maxCharacters);
    case MetricValueRole.ABSOLUTE_CHANGE:
      return formatValueAsRelativeNumber(v.value, formatString, maxCharacters);
    case MetricValueRole.PERCENTAGE_CHANGE:
      return formatValueAsRelativePercentage(v.value, maxCharacters);
  }
};

/**
 * The below code formats a number value for display as a metric. The following requirements are considered:
 * - Glanceability: Easy-to-understand scale. Number types, such as currency and percentage are present.
 * - Compactness: Ability to fit in a limited display space - phones, pills, axis ticks, etc.
 * - Correctness: Display accurately for the user's device and locale.
 *
 * To format the number we break it into four parts: sign, prefix, value, and postfix.
 * - sign: '+', '-', or '', depending on the sign of the number and the value of the 'includeSign' parameter.
 * - prefix: currency symbol, custom user input, or empty
 * - value: compact number, e.g. 1,200,000 => 1.2M.  We default to a max of two decimal places.
 * - postfix: same as prefix, but after the value.
 *
 * The prefix and postfix strings are parsed from the formatString if present.
 *
 * Once this is done we sum up the length of the parts.  If the length is not greater than the maxCharacters,
 * we concatenate the parts return the value string as-is.
 *
 * If the string is too long, we do the following:
 * - try to compact the value further by removing any decimals.
 * - trim and truncate the prefix and postfix strings.
 *
 * In all cases we keep the sign and enough of the value to accurately display the value of the number, even if
 * this means we can't fit it in the desired maxCharacters.
 */

/**
 * This function formats the value as a number with currency/percentage applied.
 * @param value The number to be formatted.
 * @param formatString A format string containing currency and other information.
 * @param includeSign specifies whether the sign of the number (+/-) should be included.
 * @param maxCharacters An optional number specifying the max number of characters for the string.
 *                 If this is non-zero, the value will be fit into this number if possible.
 */
function formatMetricValueForDisplay(
  value: number | undefined,
  formatString: string,
  includeSign: IncludeSign,
  maxCharacters?: number
): string {
  if (value === undefined) {
    return Messages.metricsNoValue();
  }

  // Determine the sign string from the value and the includeSign parameter.
  let sign = '';
  if (includeSign !== IncludeSign.NEVER) {
    if (value < 0) {
      sign = '-';
    } else if (includeSign === IncludeSign.ALWAYS) {
      sign = '+';
    }
  }

  // Parse the format string, and get the prefix and postfix strings if present.
  let prefix = '';
  let postfix = '';
  let isCurrency = false;
  let isPercentage = false;
  const formatInfo = parseFormatString(formatString);
  if (formatInfo.dataType === FormatDataType.FORMAT_NUMBER) {
    prefix = formatInfo.prefixSymbol;
    postfix = formatInfo.postfixSymbol;
    isCurrency = formatInfo.isCurrency;
    isPercentage = formatInfo.isPercentage;
  }

  // Format as a scaled positive number with grouping separators and round to max 2 decimal places.
  let scaledValueForDisplay = scaleAndFormatNumber(
    value,
    false /* don't truncate decimals */,
    isCurrency,
    isPercentage
  );

  // If needed, truncate the fractional part of the number and the prefix/postfix strings.
  if (maxCharacters && maxCharacters < scaledValueForDisplay.length + sign.length + prefix.length + postfix.length) {
    const truncatedValue = scaleAndFormatNumber(value, true /* do truncate decimals */, isCurrency, isPercentage);
    scaledValueForDisplay = fitDisplayValueToWidthInCharacters(truncatedValue, sign, prefix, postfix, maxCharacters);
  } else {
    scaledValueForDisplay = `${sign}${prefix}${scaledValueForDisplay}${postfix}`;
  }

  return scaledValueForDisplay;
}

/**
 * This method returns the scaled absolute value of the number, including the scale specifier.
 * @param value The number to be scaled
 * @param truncateDecimals if true, no decimals will be included in the string.
 */
function scaleAndFormatNumber(
  value: number,
  truncateDecimals: boolean,
  isCurrency: boolean,
  isPercentage?: boolean
): string {
  // Use the absolute value when determining the scale of the number
  const absValue = Math.abs(value);

  // Percentages are multiplied by a hundred when they are formatted. We want to preview the size
  // of the number to decide how we want to format it.
  const scaledAbsValue = absValue * (isPercentage ? 100 : 1);

  if (scaledAbsValue < 0.01) {
    // Currently we only scale large numbers.
    // Treat small numbers as 0 because we don't know how to render them.
    return isPercentage ? Formatters.formatPercentageWithNoDecimals(0) : Formatters.formatNumberWithNoDecimals(0);
  }

  if (scaledAbsValue < 10000 && isInteger(scaledAbsValue)) {
    // The raw number looks better than the scaled version
    return isPercentage
      ? Formatters.formatPercentageWithNoDecimals(absValue)
      : Formatters.formatNumberWithNoDecimals(absValue);
  }

  if (truncateDecimals) {
    return isPercentage
      ? Formatters.formatCompactPercentageWithNoDecimals(absValue)
      : Formatters.formatCompactNumberWithNoDecimals(absValue);
  }

  const compacted = isPercentage
    ? Formatters.formatCompactPercentageWithMaxTwoDecimals(absValue)
    : Formatters.formatCompactNumberWithMaxTwoDecimals(absValue);

  const maxTwoDecimals = isPercentage
    ? Formatters.formatPercentageWithMaxTwoDecimals(absValue)
    : Formatters.formatNumberWithMaxTwoDecimals(absValue);

  if (isCurrency && compacted === maxTwoDecimals) {
    return isPercentage
      ? Formatters.formatCompactPercentageWithExactlyTwoDecimals(absValue)
      : Formatters.formatCompactNumberWithExactlyTwoDecimals(absValue);
  }

  // return a compact (aka scaled) version of the number
  return compacted;
}

function fitDisplayValueToWidthInCharacters(
  numberValue: string,
  sign: string,
  prefix: string,
  postfix: string,
  width: number
): string {
  // The numberValue has already been trimmed if possible; so if the string doesn't fit in the width,
  // we need to trim the prefix and postfix strings.
  const signLength = sign.length;
  const valueLength = numberValue.length;
  const prefixLength = prefix.length;
  const postfixLength = postfix.length;

  if ((prefixLength || postfixLength) && signLength + valueLength + prefixLength + postfixLength > width) {
    // Let's distribute any remaining width between the prefix and postfix.
    const remainingWidth = Math.max(width - valueLength - signLength, 1);
    const newPrefixLength = Math.ceil((prefixLength / (prefixLength + postfixLength)) * remainingWidth);
    const newPostfixLength = Math.ceil((postfixLength / (prefixLength + postfixLength)) * remainingWidth);

    if (prefix.length > newPrefixLength) {
      prefix = prefix.trim().substr(0, newPrefixLength);
    }
    if (postfix.length > newPostfixLength) {
      postfix = postfix.trim().substr(0, newPostfixLength);
    }
  }

  return sign + prefix + numberValue + postfix;
}

const isInteger = (value: number): boolean => {
  return typeof value === 'number' && isFinite(value) && Math.floor(value) === value;
};
